import {
  Button,
  ColumnTable,
  DisplayInfo,
  Icon,
  Table,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { TranslationsKeys } from '../../../locales/translations';
import { useIsQueryEnabled } from '../hooks/useIsQueryEnabled';
import { useWarningsSummaryDataLoader } from '../loaders/StudentWarningsSummaryDataLoader';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';
import { StudentWarningsSummaryRow } from '../types';
import RiskLevelBadge from './RiskLevelBadge';
import StudentWarningsSummaryWarningsModal, {
  StudentWarningsSummaryWarningsModalMethods,
} from './StudentWarningsSummaryWarningsModal';

const StudentWarningsSummaryTable = () => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const modalRef = useRef<StudentWarningsSummaryWarningsModalMethods>(null);
  const data = useWarningsSummaryFormLoaderData();
  const {
    loading,
    query,
    result,
    updateQuery,
  } = useWarningsSummaryDataLoader();
  const isQueryEnabled = useIsQueryEnabled(query);
  const history = useHistory();

  const seeDetails = useCallback(
    (row: StudentWarningsSummaryRow) => {
      if (!isQueryEnabled || !data?.warningGroups?.length) {
        return;
      }
      if (query.warningGroupType === 'summary') {
        history.push(
          `/student-warnings/details/${row.studyPlanEnrollmentId}/${query.periodId}/${query.year}/${data.warningGroups[0].value}`,
        );
      } else {
        history.push(
          `/student-warnings/details/${row.studyPlanEnrollmentId}/${query.periodId}/${query.year}/${query.warningGroupType}`,
        );
      }
    },
    [
      history,
      query.periodId,
      query.year,
      query.warningGroupType,
      isQueryEnabled,
      data,
    ],
  );

  const columns = useMemo<ColumnTable<StudentWarningsSummaryRow>[]>(
    () => [
      {
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.name`),
        cellFormat: ({ row }) => (
          <div className="d-block text-center">
            <span className="d-block">{row.name}</span>
            <strong className="d-block">{row.identification}</strong>
          </div>
        ),
      },
      {
        columnName: 'studyPlan',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.studyPlan`),
      },
      {
        columnName: 'campus',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.campus`),
      },
      {
        columnName: 'promo',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.promo`),
      },
      {
        columnName: 'course',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.course`),
      },
      {
        columnName: 'warnings',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.warnings`),
        cellFormat: ({ row }) => {
          const warningCount = Object.values(row.warnings).reduce(
            (a, b) => a + b,
            0,
          );
          return (
            <div>
              <span>{warningCount}</span>
              {!!(warningCount && query.warningGroupType === 'summary') && (
                <button
                  className="btn btn-sm"
                  type="button"
                  onClick={() => modalRef?.current?.open(row)}
                >
                  <Icon name="information" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        columnName: 'riskLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText:
          query.warningGroupType === 'summary'
            ? t(`table.headers.riskLevel.summary`)
            : t(`table.headers.riskLevel.other`),
        cellFormat: ({ row }) => <RiskLevelBadge riskLevel={row.riskLevel} />,
      },
      {
        columnName: 'download',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.download`),
        cellFormat: () => (
          <div>
            <button type="button" className="btn btn-sm" disabled>
              <Icon name="download" color="disabled" />
            </button>
          </div>
        ),
      },
      {
        columnName: 'action',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.action`),
        cellFormat: ({ row }) => (
          <div>
            <Button
              text={t(`table.actions.manage`)}
              size="sm"
              onClick={() => seeDetails(row)}
            />
          </div>
        ),
      },
    ],
    [t, query.warningGroupType, seeDetails],
  );

  if (!isQueryEnabled) {
    return (
      <div className="mt-4 d-block">
        <DisplayInfo
          insideCard
          title={t('noFiltersSelected.title')}
          textBody={t('noFiltersSelected.description')}
        />
      </div>
    );
  }

  if (!result?.success) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Table
          data={result?.data?.data ?? []}
          columns={columns}
          isLoadingResults={loading}
          pagination={{
            itemsPerPage: query.items_per_page,
            currentPage: query.page,
            totalItems: result.data.total,
            totalPages: result.data.total_pages,
            onChangePage: (nextPage) => {
              updateQuery({
                ...query,
                page: nextPage,
              });
            },
          }}
        />
      </div>
      <StudentWarningsSummaryWarningsModal ref={modalRef} />
    </>
  );
};

export default StudentWarningsSummaryTable;
